import { useCallback, useMemo, useRef, useState } from 'react'

import type { Layout, LayoutItem } from 'react-grid-layout'

import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from '~/libs/localStorage'

type SavedPosition = Pick<LayoutItem, 'x' | 'y' | 'w' | 'h'>
type SavedLayoutMap = Record<string, SavedPosition>

type UseGridCardLayoutOptions = {
  /** localStorage キー。画面ごとに固有の値を渡す (需要 / 発電で別キー)。 */
  storageKey: string
  /**
   * 現在表示中のカードの安定 ID (= エリアコード / BG コード等。配列 index では
   * 表現しない — フィルタで可視集合が変わっても他カードへレイアウトがずれない
   * ようにするため。design js/bg-data.js / hatsu-data.js が gs-id=エリア名/BG
   * コードでキーする方式と同じ理由)。既定表示順に並んでいる前提。
   */
  cardIds: readonly string[]
  /** 未保存カードの既定サイズ (グリッド単位)。 */
  defaultSize: { w: number; h: number }
  /** リサイズ下限 (グリッド単位)。省略時は下限なし。 */
  minSize?: { w: number; h: number }
  /**
   * グリッドの列数 (`CardGridLayout` の `gridConfig.cols` と揃える。既定 12)。
   * 保存値の範囲クランプに使う。
   */
  cols?: number
}

type UseGridCardLayoutReturn = {
  layout: Layout
  /** ドラッグ / リサイズ確定時 (onDragStop / onResizeStop) に渡す。 */
  onLayoutSettled: (layout: Layout) => void
  /** カードレイアウトを初期配置に戻す (doji-bg.md AC-BDB-006 / doji-hatsu.md AC-BDH-008)。 */
  resetLayout: () => void
}

const CARDS_PER_ROW = 3
const DEFAULT_COLS = 12

/** 同時同量 - 需要 (`BalanceDemand`) のレイアウト保存キー。 */
export const BALANCE_DEMAND_LAYOUT_STORAGE_KEY = 'ebalance-doji-bg-layout-v1'
/** 同時同量 - 発電 (`BalanceGeneration`) のレイアウト保存キー。 */
export const BALANCE_GENERATION_LAYOUT_STORAGE_KEY = 'ebalance-hatsu-layout-v1'

/**
 * 保存済みカードレイアウトを全画面ぶん破棄する (ログアウト時に呼ぶ)。
 *
 * 発電側の保存値はカード ID として BG コード (= 組織のマスタ由来の業務識別子) を
 * 含むため、共用端末でログアウトしたあと DevTools や次のログインユーザーから
 * 前組織の BG コード一覧が読めてしまう。保存先が localStorage (= セッション終了で
 * 自動的には消えない) である以上、セッション終了時に明示的に破棄する必要がある。
 *
 * キー定数をページ側ではなくこの module に置いているのは、保存キーの SoT を
 * 「保存する側」に一本化し、破棄側が page module (= 画面まるごと) を import
 * せずに済むようにするため。
 *
 * NOTE: 本 hook 以外が持つ `ebalance-` / `ebalance:` prefix の既存キー
 * (spot 入札ドラフト・SupplyGap フィルタ・言語 / theme キャッシュ等) も同様に
 * ログアウト後に残るが、どれを破棄してよいか (= 入札ドラフトの破棄は業務判断、
 * 言語 / theme は first-paint キャッシュ) は本 PR の範囲を超えるため、
 * 横断的な破棄方針は follow-up とする。
 */
export function clearGridCardLayouts(): void {
  removeLocalStorageItem(BALANCE_DEMAND_LAYOUT_STORAGE_KEY)
  removeLocalStorageItem(BALANCE_GENERATION_LAYOUT_STORAGE_KEY)
}

/**
 * 保存値の y / h に許す上限 (行数)。localStorage を手で書き換えた壊れた値
 * (`y: 1e12` 等) がそのままグリッドへ渡り、コンテナが非現実的な高さで描画される
 * のを防ぐための緩い上限。1 行 70px として 1000 行 = 70,000px はどのカード数でも
 * 実運用上あり得ない値。
 */
const MAX_ROWS = 1000

/**
 * 保存 map の最大件数。**現在可視のカードは対象から除外し、非表示カードの
 * 履歴 (= フィルタ等で今は見えないが過去に動かしたカード) だけに適用する上限**
 * (`pruneOldest` の `protectedIds` 引数参照)。可視カード数がこの値を超える
 * 組織 (発電 BG は「組織あたり数十件想定」とコメントしていた帯のすぐ下) では、
 * 可視カードを削って帳尻を合わせると「いま画面に出ているカードの保存位置が
 * ドラッグのたびに消える」regression になるため、可視カードは上限を超えても
 * 常に保持する (= この上限は「無制限増加のみを防ぐ」目的の非表示カード側の
 * 上限であり、可視カード数の上限ではない)。超過分は最後に触った時刻が古い方
 * から間引く (LRU、touchEntries/pruneOldest 参照)。
 */
const MAX_SAVED_ENTRIES = 50

function defaultPositionFor(
  index: number,
  size: { w: number; h: number },
): SavedPosition {
  return {
    x: (index % CARDS_PER_ROW) * size.w,
    y: Math.floor(index / CARDS_PER_ROW) * size.h,
    w: size.w,
    h: size.h,
  }
}

/**
 * 保存済みカードが既に存在する状態で新たに現れた (= 未保存の) カードの配置。
 * 保存済みカードの領域 (y = 0 〜 保存済みの最大 y+h) には重ねず、その下から
 * 3 列で詰める。保存済みカードが 1 件も無い場合は呼ばない (= その場合は
 * `defaultPositionFor` で cardIds の並び順どおりに敷き詰める)。
 *
 * AC-BDB-012「レイアウト変更後はユーザー保存順を優先」: 新規カードの出現で
 * 既存の保存済み配置が (vertical compact により) 押し出されて食い違うのを防ぐ。
 */
function unsavedPositionBelow(
  unsavedIndex: number,
  savedMaxY: number,
  size: { w: number; h: number },
): SavedPosition {
  return {
    x: (unsavedIndex % CARDS_PER_ROW) * size.w,
    y: savedMaxY + Math.floor(unsavedIndex / CARDS_PER_ROW) * size.h,
    w: size.w,
    h: size.h,
  }
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

/**
 * 保存値の座標を妥当な範囲へクランプする。範囲外エントリを丸ごと捨てるのでは
 * なくクランプする方針にした (= 意図した位置をできるだけ保つ。座標が多少
 * 削れる程度なら「既定配置に戻る」より驚きが小さい)。
 *
 * `minSize` が指定されている場合は `w`/`h` の下限にも適用する — これが無いと
 * (手で書き換えた・古いバージョンで保存された等の) `h:1` のような保存値が
 * そのまま `layout` に混ざり、`{h:1, minH:3}` という自己矛盾した item になる。
 * `MIN_CARD_SIZE` (#4717 のチャート潰れ回帰防止) の意図は保存値経由でも
 * 常に守られる必要がある。
 */
function clampSavedPosition(
  pos: SavedPosition,
  cols: number,
  minSize?: { w: number; h: number },
): SavedPosition {
  const minW = Math.max(minSize?.w ?? 1, 1)
  const minH = Math.max(minSize?.h ?? 1, 1)
  const w = Math.min(Math.max(Math.round(pos.w), minW), cols)
  const h = Math.min(Math.max(Math.round(pos.h), minH), MAX_ROWS)
  const x = Math.min(Math.max(Math.round(pos.x), 0), cols - w)
  const y = Math.min(Math.max(Math.round(pos.y), 0), MAX_ROWS)
  return { x, y, w, h }
}

/** localStorage の保存形式: `[[id, x, y, w, h], ...]` (コンパクトな配列形式)。 */
function parseSavedLayout(
  raw: string | undefined,
  cols: number,
  minSize?: { w: number; h: number },
): SavedLayoutMap {
  if (raw === undefined) return {}

  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return {}

    const map: SavedLayoutMap = {}
    for (const entry of parsed) {
      if (!Array.isArray(entry) || entry.length !== 5) continue
      const [id, x, y, w, h] = entry as unknown[]
      if (typeof id !== 'string') continue
      if (
        !isFiniteNumber(x) ||
        !isFiniteNumber(y) ||
        !isFiniteNumber(w) ||
        !isFiniteNumber(h)
      ) {
        continue
      }
      map[id] = clampSavedPosition({ x, y, w, h }, cols, minSize)
    }
    return map
  } catch {
    return {}
  }
}

function serializeSavedLayout(map: SavedLayoutMap): string {
  return JSON.stringify(
    Object.entries(map).map(([id, pos]) => [id, pos.x, pos.y, pos.w, pos.h]),
  )
}

/**
 * 直近に触れた id を map の末尾 (= 挿入順で最後) に押し出す。JS の plain object
 * は string key を挿入順で反復するため、「既存 key を一度 delete してから
 * 入れ直す」ことで挿入順を「直近に触れた順」に保てる (= 簡易 LRU の順序管理。
 * `pruneOldest` が先頭 = 最も長く触られていない key から間引く)。
 *
 * **前提**: `cardIds` に整数 index として解釈できる文字列 (`"12345"` 等) を
 * 渡さないこと。JS の plain object はその種の key を挿入順より先に昇順で反復する
 * ため、delete → 再代入しても順序が変わらず LRU が崩れる。現行の呼び出し側は
 * どちらもこの前提を満たす: 発電の BG コードは OCCTO 形式で先頭が英字 `G`/`L`/`D`
 * に固定され (`go/internal/backend/domain/valueobject/bg_code.go` の
 * `^G[A-HJ-NP-Z][A-HJ-NP-Z0-9]{2}\d$` 等、2〜4 文字目は I/O 除外)、需要は
 * `AreaCode` の enum 名。
 */
function touchEntries(map: SavedLayoutMap, settled: Layout): SavedLayoutMap {
  const next = { ...map }
  for (const item of settled) {
    delete next[item.i]
    next[item.i] = { x: item.x, y: item.y, w: item.w, h: item.h }
  }
  return next
}

/**
 * `MAX_SAVED_ENTRIES` を超えた分を、挿入順が古い (= 最後に触った時刻が古い) 方
 * から間引く。`protectedIds` (= 現在可視のカード ID) に含まれる key は間引き
 * 対象から除外する — 除外した結果、上限を超えたまま返ることがある (R2:
 * 可視カードは常に保持する不変条件を、件数上限より優先する)。
 */
function pruneOldest(
  map: SavedLayoutMap,
  protectedIds: ReadonlySet<string>,
): SavedLayoutMap {
  const keys = Object.keys(map)
  if (keys.length <= MAX_SAVED_ENTRIES) return map

  const next = { ...map }
  let remainingToPrune = keys.length - MAX_SAVED_ENTRIES
  for (const key of keys) {
    if (remainingToPrune <= 0) break
    if (protectedIds.has(key)) continue
    delete next[key]
    remainingToPrune -= 1
  }
  return next
}

/**
 * 同時同量ダッシュボード (需要 / 発電) 共通のカードグリッドレイアウト永続化 hook。
 *
 * - 保存先は localStorage (ユーザー指示による確定事項。~/libs/localStorage 参照)
 * - 保存対象はカード安定 ID ごとの `{x, y, w, h}` のみ
 * - フィルタ等で一時的に非表示になったカードの保存済み位置は保持する (= 再表示時
 *   に元の配置へ戻る。保存済みだが現在非表示の ID を保存 map から間引かない —
 *   件数上限 `MAX_SAVED_ENTRIES` は非表示カードの履歴にのみ適用し、現在可視の
 *   カードは上限を超えても間引かない (R2))
 * - 保存済みカードが 1 件でもある状態で未保存カードが現れた場合、未保存カードは
 *   保存済みカードの下 (`unsavedPositionBelow` 参照) に配置する。保存済みが
 *   0 件の時のみ、cardIds の並び順から index%3/floor(index/3) で配置する
 *   (design defaultLayoutFor 準拠)
 */
export function useGridCardLayout({
  storageKey,
  cardIds,
  defaultSize,
  minSize,
  cols = DEFAULT_COLS,
}: UseGridCardLayoutOptions): UseGridCardLayoutReturn {
  const [savedMap, setSavedMap] = useState<SavedLayoutMap>(() =>
    parseSavedLayout(getLocalStorageItem(storageKey), cols, minSize),
  )
  // クロージャの陳腐化対策 (R7): `onLayoutSettled` は React の再レンダーを待たず
  // `savedMap` の最新値を読む必要がある。同一 tick 内で 2 回確定した場合、2 回目
  // の呼び出し時点では `setSavedMap` の結果がまだ state に反映されておらず
  // (= コンポーネントが再レンダーされておらず)、`useCallback` が閉じ込めた
  // `savedMap` は 1 回目の呼び出し前の値のまま — つまり 1 回目の更新を
  // 取りこぼす (lost update)。ref は `setState` を待たず同期的に更新できるため、
  // ここに常に最新値を保つ。
  const savedMapRef = useRef(savedMap)
  savedMapRef.current = savedMap

  const layout = useMemo<Layout>(() => {
    const savedEntries = Object.values(savedMap)
    const savedMaxY =
      savedEntries.length > 0
        ? Math.max(...savedEntries.map((pos) => pos.y + pos.h))
        : 0

    let unsavedIndex = 0
    return cardIds.map((id, index) => {
      const saved = savedMap[id]
      let position: SavedPosition
      if (saved !== undefined) {
        position = saved
      } else if (savedEntries.length > 0) {
        position = unsavedPositionBelow(unsavedIndex, savedMaxY, defaultSize)
        unsavedIndex += 1
      } else {
        position = defaultPositionFor(index, defaultSize)
      }

      return {
        i: id,
        ...position,
        ...(minSize ? { minW: minSize.w, minH: minSize.h } : {}),
      }
    })
  }, [cardIds, savedMap, defaultSize, minSize])

  const onLayoutSettled = useCallback(
    (settled: Layout) => {
      // 永続化 (副作用) は setState の updater の外で行う — updater は React が
      // 再実行しうる前提の純関数であるべきなので、次の map を先に組んでから
      // setSavedMap と保存呼び出しを並べる (R8 の指摘対応。updater 内へは
      // 戻さない)。base は `savedMap` (state) ではなく `savedMapRef.current`
      // を読む (R7: 同一 tick 内の 2 回目の確定でも最新値を読むため)。
      // R2: pruneOldest には現在可視の cardIds を「間引かない集合」として渡す
      // — 可視カードが上限を超えていても、可視カードの保存位置は落とさない。
      const next = pruneOldest(
        touchEntries(savedMapRef.current, settled),
        new Set(cardIds),
      )
      savedMapRef.current = next
      setSavedMap(next)
      setLocalStorageItem(storageKey, serializeSavedLayout(next))
    },
    [storageKey, cardIds],
  )

  const resetLayout = useCallback(() => {
    savedMapRef.current = {}
    setSavedMap({})
    removeLocalStorageItem(storageKey)
  }, [storageKey])

  return { layout, onLayoutSettled, resetLayout }
}
